import React, { useEffect, useMemo, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { Compass, Sun, Tent, Sparkles, Check, ArrowRight, Clock, Zap, Flame } from "lucide-react";
import { api } from "../lib/api";

/**
 * /share/launch-push — six 9:16 reel scenes specifically engineered for the
 * final-week push before the 31 May 2026 launch.
 *
 * Difference vs /share/reels-kit:
 *   • LIVE COUNTDOWN — every scene shows "X DAYS LEFT" auto-computed from
 *     LAUNCH_DATE so the urgency stays accurate as the launch approaches.
 *   • EVERY scene has a giant "SIGN UP NOW" CTA pinned to the bottom.
 *   • Every QR code points at /register (not the landing) so a single scan
 *     drops the user straight on the signup form.
 *   • Brighter, sunnier camping photo set (Pexels CDN, no attribution).
 *   • Founder-member angle baked in — "first 1,000 get…" / "before launch"
 *     hooks targeting bank-holiday weekend sign-up surge.
 *
 * Workflow: open this page on your phone or in a 1080×1920 desktop window
 * → screen-record while scrolling through each scene → trim into a Reel,
 * post on Instagram / TikTok / FB Reels.
 */

const PUBLIC_URL = (process.env.REACT_APP_PUBLIC_URL || "https://camp-compass.uk").replace(/\/$/, "");
const SIGNUP_URL = `${PUBLIC_URL}/register?ref=launch-push-reel`;
const LAUNCH_DATE = new Date("2026-05-31T00:00:00Z");

// Bright, sunny UK / outdoor camping photos from Pexels (free for commercial
// use, no attribution required). Hand-picked for high contrast + sunny vibe.
const PHOTOS = {
  // bright yellow tent, sun streaming through trees
  countdown:    "https://images.pexels.com/photos/1687845/pexels-photo-1687845.jpeg?auto=compress&cs=tinysrgb&w=1080&h=1920&fit=crop",
  // panoramic sunny mountain camp scene
  founder:      "https://images.pexels.com/photos/2174656/pexels-photo-2174656.jpeg?auto=compress&cs=tinysrgb&w=1080&h=1920&fit=crop",
  // sunny grass valley with tent
  fomo:         "https://images.pexels.com/photos/2422915/pexels-photo-2422915.jpeg?auto=compress&cs=tinysrgb&w=1080&h=1920&fit=crop",
  // bright meadow with golden light
  first1000:    "https://images.pexels.com/photos/1670045/pexels-photo-1670045.jpeg?auto=compress&cs=tinysrgb&w=1080&h=1920&fit=crop",
  // sunny couple at campsite (dream sale)
  yourpitch:    "https://images.pexels.com/photos/2422588/pexels-photo-2422588.jpeg?auto=compress&cs=tinysrgb&w=1080&h=1920&fit=crop",
  // bright signup hero
  tapjoin:      "https://images.pexels.com/photos/2422265/pexels-photo-2422265.jpeg?auto=compress&cs=tinysrgb&w=1080&h=1920&fit=crop",
};

function Scene({ bg, gradient, children, index }) {
  return (
    <section
      className="relative w-full mx-auto flex flex-col justify-between text-white overflow-hidden"
      style={{
        aspectRatio: "9 / 16",
        maxWidth: 540,
        background: gradient,
        backgroundImage: `linear-gradient(180deg, rgba(0,0,0,0.30) 0%, rgba(0,0,0,0.05) 35%, rgba(0,0,0,0.70) 100%), url("${bg}")`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
      data-testid={`launch-push-scene-${index}`}
    >
      {children}
    </section>
  );
}

function Sticker({ children, className = "", color = "#ff4500" }) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-3 py-1 text-white text-[11px] font-bold uppercase tracking-[0.2em] ${className}`}
      style={{ background: color }}
    >
      {children}
    </span>
  );
}

/** The SIGN UP NOW CTA pinned at the bottom of every scene. */
function SignupCTA({ daysLeft, variant = "orange" }) {
  const bg = variant === "white" ? "#ffffff" : "#ff4500";
  const fg = variant === "white" ? "#0e0e0e" : "#ffffff";
  return (
    <div className="p-5 sm:p-6 space-y-3">
      <div className="text-center font-display text-[42px] sm:text-[56px] leading-none drop-shadow-lg text-white">
        SIGN UP NOW
      </div>
      <div className="text-center text-[13px] sm:text-base font-bold uppercase tracking-wider drop-shadow" style={{ color: "#ffe7d6" }}>
        Free to sign up · 20 seconds
      </div>
      <div className="flex items-center justify-between gap-4 pt-2">
        <div className="text-left">
          <div
            className="inline-flex items-center gap-1.5 px-2 py-1 font-bold uppercase text-[10px] tracking-[0.18em] mono mb-2"
            style={{ background: bg, color: fg }}
          >
            <Clock className="w-3 h-3" /> {daysLeft} days to launch
          </div>
          <div className="text-[10px] mono uppercase tracking-[0.25em] text-white/90 mb-1">Scan or visit</div>
          <div className="font-display text-base sm:text-lg">camp-compass.uk/register</div>
        </div>
        <div className="bg-white p-1.5 shrink-0">
          <QRCodeSVG value={SIGNUP_URL} size={92} bgColor="#ffffff" fgColor="#111111" includeMargin={false} />
        </div>
      </div>
    </div>
  );
}

export default function LaunchPush() {
  const [count, setCount] = useState(14000);
  useEffect(() => {
    let alive = true;
    api.get("/campsites/stats").then(({ data }) => {
      if (alive && data?.total) setCount(data.total);
    }).catch(() => {});
    return () => { alive = false; };
  }, []);

  const daysLeft = useMemo(() => {
    const ms = LAUNCH_DATE.getTime() - Date.now();
    return Math.max(0, Math.ceil(ms / 86_400_000));
  }, []);

  const fmt = useMemo(() => count.toLocaleString("en-GB"), [count]);

  return (
    <div className="bg-[#0e0e0e] min-h-screen text-white" data-testid="launch-push-page">
      <header className="max-w-3xl mx-auto px-5 py-8">
        <div className="eyebrow text-[#ff4500] mb-2 flex items-center gap-2">
          <Zap className="w-3.5 h-3.5" /> Launch Push · {daysLeft} days to go
        </div>
        <h1 className="font-display text-3xl sm:text-4xl mb-3">Final-week reels.<br/>Every scene = SIGN UP NOW.</h1>
        <p className="text-sm text-[#bbb] leading-relaxed max-w-xl">
          Six bright, sunny, signup-first reels engineered for the last week before the
          <strong> 31 May 2026 launch</strong>. Every QR code drops users straight on the registration
          form. Scroll through, screen-record each one, post Thursday → Friday → Saturday → Sunday
          for max momentum into launch day.
        </p>
      </header>

      <div className="flex flex-col items-center gap-12 px-3 pb-12">

        {/* 1 — COUNTDOWN HERO */}
        <Scene index="1" bg={PHOTOS.countdown} gradient="linear-gradient(180deg,#ffb449 0%,#ff4500 100%)">
          <div className="p-6 sm:p-8 pt-12">
            <Sticker><Sun className="w-3.5 h-3.5" /> The clock is ticking</Sticker>
          </div>
          <div className="px-6 sm:px-8 text-center">
            <div className="font-display text-[100px] sm:text-[140px] leading-none mb-2 drop-shadow-lg">
              {daysLeft}
            </div>
            <div className="font-display text-3xl sm:text-4xl mb-4 drop-shadow">
              DAYS LEFT
            </div>
            <div className="text-base sm:text-xl font-bold leading-snug text-[#ffe7d6] drop-shadow">
              Until the UK's biggest campsite app launches.<br/>{fmt} sites. One map. Yours to explore.
            </div>
          </div>
          <SignupCTA daysLeft={daysLeft} />
        </Scene>

        {/* 2 — THE BIGGEST UK CAMPSITE MAP */}
        <Scene index="2" bg={PHOTOS.founder} gradient="linear-gradient(180deg,#fbbf24 0%,#d97706 100%)">
          <div className="p-6 sm:p-8 pt-12">
            <Sticker color="#0e0e0e"><Tent className="w-3.5 h-3.5" /> Every corner of the UK</Sticker>
          </div>
          <div className="px-6 sm:px-8 text-center">
            <div className="font-display text-[80px] sm:text-[120px] leading-none mb-1 drop-shadow-lg">
              {fmt}
            </div>
            <div className="font-display text-2xl sm:text-3xl mb-4">UK campsites.</div>
            <div className="text-base sm:text-xl font-bold leading-snug text-white/95 mb-3">
              All on one map. No app required.
            </div>
            <div className="text-sm sm:text-base text-white/85 leading-relaxed max-w-[30ch] mx-auto">
              Lakes. Coast. Highlands. Forest. Farm. Bothies. Glamping pods. Every kind of pitch.
            </div>
          </div>
          <SignupCTA daysLeft={daysLeft} />
        </Scene>

        {/* 3 — DON'T MISS LAUNCH (FOMO) */}
        <Scene index="3" bg={PHOTOS.fomo} gradient="linear-gradient(180deg,#ff7e3b 0%,#dc2626 100%)">
          <div className="p-6 sm:p-8 pt-12">
            <Sticker><Flame className="w-3.5 h-3.5" /> Bank Holiday alert</Sticker>
          </div>
          <div className="px-6 sm:px-8 text-center">
            <div className="font-display text-[44px] sm:text-[62px] leading-[0.95] mb-4 drop-shadow-lg">
              Don't<br/>queue at<br/>launch.
            </div>
            <div className="text-base sm:text-xl font-bold leading-snug text-[#ffe7d6] drop-shadow mb-3">
              Install + sign up in 20 seconds today.
            </div>
            <div className="text-sm sm:text-base text-white/85 leading-relaxed max-w-[30ch] mx-auto">
              Launch day = your phone is already set up. No app store wait. No signup queue. Just go camping.
            </div>
          </div>
          <SignupCTA daysLeft={daysLeft} />
        </Scene>

        {/* 4 — 60-SECOND SEARCH */}
        <Scene index="4" bg={PHOTOS.first1000} gradient="linear-gradient(180deg,#fcd34d 0%,#f59e0b 100%)">
          <div className="p-6 sm:p-8 pt-12">
            <Sticker color="#0e0e0e"><Sparkles className="w-3.5 h-3.5" /> Built for one job</Sticker>
          </div>
          <div className="px-6 sm:px-8 text-center">
            <div className="font-display text-[36px] sm:text-[52px] leading-[1] mb-4 drop-shadow-lg">
              Find your<br/>perfect pitch<br/>in <span className="text-[#ff4500]">60 seconds</span>.
            </div>
            <div className="text-base sm:text-xl font-bold leading-snug text-white/95 mb-3">
              Filter by price, dogs, hot showers, kid-friendly.
            </div>
            <div className="text-sm sm:text-base text-white/85 leading-relaxed max-w-[30ch] mx-auto">
              We did the boring research across {fmt} sites. You get the answer.
            </div>
          </div>
          <SignupCTA daysLeft={daysLeft} variant="white" />
        </Scene>

        {/* 5 — YOUR PITCH IS WAITING (dream sale) */}
        <Scene index="5" bg={PHOTOS.yourpitch} gradient="linear-gradient(180deg,#fb923c 0%,#ea580c 100%)">
          <div className="p-6 sm:p-8 pt-12">
            <Sticker><Tent className="w-3.5 h-3.5" /> Pick yours now</Sticker>
          </div>
          <div className="px-6 sm:px-8 text-center">
            <div className="font-display text-[42px] sm:text-[58px] leading-[0.95] mb-4 drop-shadow-lg">
              {fmt}<br/>pitches.<br/>
              <span className="text-[#fff3e0]">Yours is one of them.</span>
            </div>
            <div className="text-base sm:text-xl font-bold leading-snug text-white/95 mb-3">
              Lakes. Coast. Highlands. Forest. Farm.
            </div>
            <div className="text-sm sm:text-base text-white/85 leading-relaxed max-w-[30ch] mx-auto">
              Filter by what matters: price, dogs, hot showers, kid-friendly, year-round.
            </div>
          </div>
          <SignupCTA daysLeft={daysLeft} />
        </Scene>

        {/* 6 — TAP TO JOIN (final CTA) */}
        <Scene index="6" bg={PHOTOS.tapjoin} gradient="linear-gradient(180deg,#ffe7d6 0%,#ff4500 100%)">
          <div className="p-6 sm:p-8 pt-12">
            <Sticker color="#0e0e0e"><Compass className="w-3.5 h-3.5" /> Open today</Sticker>
          </div>
          <div className="px-6 sm:px-8 text-center">
            <div className="font-display text-[48px] sm:text-[68px] leading-[0.92] mb-4 drop-shadow-lg">
              Tap.<br/>Sign up.<br/>Go camping.
            </div>
            <div className="text-base sm:text-xl font-bold leading-snug text-white/95 mb-3">
              That's the whole pitch.
            </div>
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white text-[#ff4500] font-bold uppercase tracking-wider text-xs">
              <Check className="w-3.5 h-3.5" /> No app store needed <ArrowRight className="w-3.5 h-3.5" />
            </div>
          </div>
          <SignupCTA daysLeft={daysLeft} />
        </Scene>
      </div>

      {/* CAPTION + HASHTAG PACK */}
      <section className="max-w-3xl mx-auto px-5 pb-20 space-y-8" data-testid="launch-push-scripts">
        <div className="border-l-2 border-[#ff4500] pl-4">
          <h2 className="font-display text-2xl mb-2">Caption + hashtag pack</h2>
          <p className="text-sm text-[#bbb]">
            Copy-paste straight into Instagram / TikTok / Facebook Reels.
            Post one per day this week → final scene on launch day at 8am.
          </p>
        </div>

        {[
          {
            title: `1 · ${daysLeft}-Day Countdown (POST TODAY)`,
            hook: `${daysLeft} days until launch.`,
            caption: `${daysLeft} days until the UK's biggest campsite app launches. 🚀⛺\n\nWe've mapped ${fmt} UK campsites. Real prices. Real reviews. One tap to book.\n\nSign up TODAY and you'll be ready to find a pitch the second we go live on 31 May.\n\n🔗 camp-compass.uk/register\n\n#campinguk #ukcamping #campsite #countdown #launch #bankholidayweekend #campervan #campingfamily`,
            hashtags: "#campinguk #ukcamping #campsite #countdown #launch #bankholidayweekend #campervan #campingfamily #ukstaycation #campingtrip #earlyaccess",
            best: "TODAY at 5-7pm — peak weekday browsing window",
            sound: "Cinematic countdown / sunny acoustic build",
          },
          {
            title: "2 · The Biggest UK Map (POST TOMORROW)",
            hook: `${fmt} UK campsites. All on one map.`,
            caption: `${fmt} UK campsites. All on one map. 🗺️⛺\n\nLakes. Coast. Highlands. Forest. Farm. Bothies. Glamping pods. Every kind of pitch in the UK — finally in one place.\n\nMost booking sites only show you the chains. We pulled in every farm site, wild pitch and mountain hideaway we could find.\n\nSign up now and the map's yours from launch morning.\n\n🔗 camp-compass.uk/register`,
            hashtags: "#campinguk #ukcamping #campsite #ukstaycation #campervanlife #glamping #campingfamily #campingweekend #wildcamping #ukcampsites #travelhack",
            best: "Tomorrow 6-8pm — evening planning peak",
            sound: "Warm cinematic / aerial map swoop",
          },
          {
            title: "3 · Don't Queue At Launch (POST FRIDAY)",
            hook: "Don't queue at launch.",
            caption: `Don't queue at launch. ⏰\n\nWhen we go live on 31 May, every camper in the UK is going to be searching for a bank-holiday pitch on the same morning.\n\nSign up TODAY → launch morning you're already booking while everyone else is signing up.\n\n20 seconds. No download. Just open camp-compass.uk in your browser.\n\n🔗 camp-compass.uk/register`,
            hashtags: "#bankholidayweekend #campingfomo #ukcamping #campervanlife #lastminutebooking #campsite #campingweekend #ukstaycation",
            best: "Friday 5pm — pre-weekend peak",
            sound: "Fast-cut energetic / chase",
          },
          {
            title: "4 · 60-Second Search (POST SATURDAY)",
            hook: "Find your perfect pitch in 60 seconds.",
            caption: `Find your perfect pitch in 60 seconds. ⏱️\n\nFilter by price, dogs, hot showers, kid-friendly, year-round, EHU.\n\nWe did the boring research across ${fmt} UK sites. You get the answer.\n\nStop opening 30 tabs to plan one weekend.\n\n🔗 camp-compass.uk/register`,
            hashtags: "#productivityhack #campinghacks #travelhack #weekendtrip #ukcamping #campervanlife #campinguk #campinglife #compareprices",
            best: "Saturday 11am — Saturday-morning research peak",
            sound: "Quick-cut sound with claps or 'aha' moment",
          },
          {
            title: "5 · Your Pitch Is Waiting (POST SUNDAY)",
            hook: `${fmt} pitches. Yours is one of them.`,
            caption: `${fmt} pitches. Yours is one of them. 🏕️\n\nLakes. Coast. Highlands. Forest. Farm. Glamping pods. Wild bothies. All on one map.\n\nFilter by what matters to you: price, dogs, hot showers, kid-friendly, year-round, EHU, accessibility.\n\nSign up now, ready for launch.\n\n🔗 camp-compass.uk/register`,
            hashtags: "#campinguk #ukcamping #campsite #dreamcamping #campervanlife #wildcamping #glamping #ukstaycation #campingfamily #campingholidays",
            best: "Sunday 7pm — Monday-planning surge",
            sound: "Soft cinematic / golden hour",
          },
          {
            title: "6 · Launch Day (POST 31 MAY 8AM)",
            hook: "Tap. Sign up. Go camping.",
            caption: `We're LIVE. ⛺🚀\n\nThe UK's biggest campsite app is open. ${fmt} sites. Real prices. Real reviews. One tap to book.\n\nTap. Sign up. Go camping. That's the whole pitch.\n\nNo app store needed — just open in your browser.\n\n🔗 camp-compass.uk/register`,
            hashtags: "#launchday #campinguk #ukcamping #campsite #campingapp #campervanlife #campingfamily #ukstaycation #wearelive",
            best: "31 May at 8am — launch hero post",
            sound: "Triumphant / launch / celebration",
          },
        ].map((s, i) => (
          <article key={i} className="p-5 border hairline bg-[#1a1a1a]" data-testid={`launch-push-script-${i + 1}`}>
            <div className="text-[10px] mono uppercase tracking-[0.25em] text-[#ff4500] mb-2">{s.title}</div>
            <div className="font-display text-lg mb-3">{s.hook}</div>
            <div className="bg-[#0e0e0e] p-3 mb-3 whitespace-pre-wrap text-sm leading-relaxed">{s.caption}</div>
            <div className="grid sm:grid-cols-2 gap-3 text-xs">
              <div className="bg-[#0e0e0e] p-3">
                <div className="eyebrow text-[#888] mb-1">Hashtags</div>
                <div className="text-[#bbb] break-words leading-relaxed">{s.hashtags}</div>
              </div>
              <div className="space-y-2">
                <div className="bg-[#0e0e0e] p-3">
                  <div className="eyebrow text-[#888] mb-1">Best time to post</div>
                  <div className="text-white">{s.best}</div>
                </div>
                <div className="bg-[#0e0e0e] p-3">
                  <div className="eyebrow text-[#888] mb-1">Sound vibe</div>
                  <div className="text-white">{s.sound}</div>
                </div>
              </div>
            </div>
          </article>
        ))}

        <div className="p-5 border-2 border-[#ff4500] bg-[#1a1a1a]" data-testid="launch-push-tips">
          <h3 className="font-display text-xl mb-2">Daily posting schedule</h3>
          <ul className="space-y-2 text-sm text-[#ddd] leading-relaxed list-decimal pl-5">
            <li><strong>Reel 1 (Countdown) — TODAY 5-7pm</strong> — get the launch on the radar.</li>
            <li><strong>Reel 2 (Biggest UK Map) — TOMORROW 6-8pm</strong> — flex the catalogue.</li>
            <li><strong>Reel 3 (Don't Queue At Launch) — FRIDAY 5pm</strong> — urgency hook.</li>
            <li><strong>Reel 4 (60-Second Search) — SATURDAY 11am</strong> — Saturday morning planning.</li>
            <li><strong>Reel 5 (Your Pitch Is Waiting) — SUNDAY 7pm</strong> — Monday-planning surge.</li>
            <li><strong>Reel 6 (Launch Day) — 31 MAY 8am</strong> — launch-day hero.</li>
          </ul>
        </div>
      </section>
    </div>
  );
}
